import { useEffect, useState } from 'react'
import { ApiError, type SeriesPublicationPreviewOutput } from '@schemas'
import { usePublicationMutation, useSeriesPublication } from '@/api/series-publication'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function PublicationControls({ showId, year }: { showId: string; year: number }) {
  const status = useSeriesPublication(showId)
  const mutation = usePublicationMutation(showId)
  const [preview, setPreview] = useState<SeriesPublicationPreviewOutput | null>(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const [previewFinalized, setPreviewFinalized] = useState<boolean | null>(null)
  const [withdrawing, setWithdrawing] = useState<number | null>(null)
  const [message, setMessage] = useState('')
  const [expired, setExpired] = useState(false)
  useEffect(() => {
    if (!preview) return
    const expire = () => {
      setExpired(true)
      setAcknowledged(false)
    }
    const remaining = Date.parse(preview.expiresAt) - Date.now()
    if (remaining <= 0) {
      expire()
      return
    }
    const timer = window.setTimeout(expire, remaining)
    return () => window.clearTimeout(timer)
  }, [preview])
  useEffect(() => {
    if (
      status.isError ||
      (preview &&
        status.data &&
        (status.data.generation !== preview.generation ||
          status.data.finalized !== previewFinalized ||
          !status.data.canManage))
    ) {
      setPreview(null)
      setAcknowledged(false)
    }
  }, [status.isError, status.data, preview, previewFinalized])
  async function act(action: Parameters<typeof mutation.mutateAsync>[0]) {
    setMessage('')
    try {
      const result = await mutation.mutateAsync(action)
      if ('payload' in result) {
        setPreviewFinalized(status.data?.finalized ?? null)
        setPreview(result)
        setAcknowledged(false)
        setExpired(false)
      } else {
        setPreview(null)
        setWithdrawing(null)
        setMessage(result.publicationId ? 'Publication saved.' : 'Publication withdrawn.')
      }
    } catch (error) {
      if (error instanceof ApiError && [400, 401, 403, 404, 409].includes(error.status)) {
        setPreview(null)
        setAcknowledged(false)
        setWithdrawing(null)
        setMessage(
          error.status === 409
            ? 'Publication or source data changed. Review a fresh preview before trying again.'
            : 'This action is unavailable. Reload the publication state and check your access.'
        )
      } else
        setMessage(
          'The request could not be confirmed. Check the publication state or retry the same action; no success has been assumed.'
        )
    }
  }
  const state = status.data
  if (status.isError)
    return (
      <div role="alert">
        Publication state is unavailable.{' '}
        <Button variant="outline" onClick={() => void status.refetch()}>
          Reload publication state
        </Button>
      </div>
    )
  if (!state) return <p role="status">Loading publication state…</p>
  const busy = mutation.isPending || status.isFetching
  return (
    <section
      aria-label={`Publication for ${year}`}
      className="space-y-3 rounded border p-4 min-w-0"
    >
      <h3 className="font-semibold">{year} publication</h3>
      <p>
        {state.activePublication
          ? `Published ${new Date(state.activePublication.publishedAt).toLocaleString()}`
          : 'Not published'}
      </p>
      <p className="text-sm text-muted-foreground">
        {state.finalized
          ? 'Finalized: eligible for publication.'
          : 'Finalize this edition before publishing. You can preview it now.'}{' '}
        Reopening or removing series membership withdraws publication. Finalizing again does not
        republish.
      </p>
      <a className="underline" href={`/public/show-series/${encodeURIComponent(state.publicSlug)}`}>
        View public series report
      </a>
      {state.canManage && (
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy} variant="outline" onClick={() => void act({ kind: 'preview' })}>
            {state.activePublication ? 'Preview republication' : 'Preview publication'}
          </Button>
          {state.activePublication && (
            <Button
              disabled={busy}
              variant="outline"
              onClick={() => setWithdrawing(state.generation)}
            >
              Unpublish
            </Button>
          )}
        </div>
      )}
      {withdrawing !== null && state.canManage && (
        <div role="group" aria-label="Confirm withdrawal" className="space-y-2">
          <p>Withdraw this edition from the public report?</p>
          <Button
            disabled={busy}
            onClick={() => void act({ kind: 'withdraw', generation: withdrawing })}
          >
            Confirm unpublish
          </Button>{' '}
          <Button disabled={busy} variant="outline" onClick={() => setWithdrawing(null)}>
            Cancel
          </Button>
        </div>
      )}
      {preview && state.canManage && (
        <div className="space-y-3" aria-label="Publication preview">
          <h4 className="font-semibold">
            Review: {preview.payload.series.name} · {preview.payload.year}
          </h4>
          <p>
            This is the exact content to publish. Publication time is assigned when publication
            succeeds. Preview expires {new Date(preview.expiresAt).toLocaleTimeString()}.
          </p>
          <dl className="grid grid-cols-2 gap-2">
            <dt>Entries</dt>
            <dd>{preview.payload.metrics.entriesCount}</dd>
            <dt>Entrants</dt>
            <dd>{preview.payload.metrics.entrantsCount}</dd>
            <dt>Entries per entrant</dt>
            <dd>{preview.payload.metrics.averageEntriesPerEntrant ?? 'Unavailable'}</dd>
            <dt>Participating clubs</dt>
            <dd>{preview.payload.metrics.clubsCount ?? 'Unavailable'}</dd>
            {preview.payload.entrantHistory ? (
              <>
                <dt>Returning entrants</dt>
                <dd>{preview.payload.entrantHistory.returning}</dd>
                <dt>First-time entrants</dt>
                <dd>{preview.payload.entrantHistory.firstTime}</dd>
                <dt>Entrants not linked to a person</dt>
                <dd>{preview.payload.entrantHistory.unlinked}</dd>
              </>
            ) : (
              <>
                <dt>Returning and first-time entrants</dt>
                <dd>Unavailable</dd>
              </>
            )}
          </dl>
          <p>
            {preview.payload.entrantHistory
              ? `Returning means the entrant's person record also has an entry in ${
                  preview.payload.entrantHistory.earlierEditions === 1
                    ? 'the 1 earlier finalized edition'
                    : `one of the ${preview.payload.entrantHistory.earlierEditions} earlier finalized editions`
                } of this series. A person recorded twice counts as first-time: merge duplicates in People, then preview again.`
              : 'The returning and first-time split needs an earlier finalized edition of this series with entries.'}
          </p>
          <ul>
            {(['assigned', 'unresolved', 'unmatched'] as const).map(key => (
              <li key={key}>
                {key === 'assigned'
                  ? 'Confirmed'
                  : key === 'unmatched'
                    ? 'Explicitly unmatched'
                    : 'Unresolved'}
                : {preview.payload.metrics.coverage[key].classes} classes /{' '}
                {preview.payload.metrics.coverage[key].entries} entries
              </li>
            ))}
          </ul>
          <Table tabIndex={0}>
            <caption>Confirmed class trends to publish</caption>
            <TableHeader>
              <TableRow>
                {['Shared label', 'Class', 'Division', 'Entries', 'Model class key'].map(h => (
                  <TableHead scope="col" key={h}>
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.payload.trends.map(t => (
                <TableRow key={t.trendKey}>
                  <TableHead scope="row">{t.label}</TableHead>
                  <TableCell>
                    {t.class.code} · {t.class.effectiveName}
                  </TableCell>
                  <TableCell>
                    {t.class.division.code} · {t.class.division.name}
                  </TableCell>
                  <TableCell>{t.class.count}</TableCell>
                  <TableCell>{t.class.modelClassKey ?? 'No validated model link'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!preview.payload.trends.length && (
            <p>
              No confirmed class trends. Only overall metrics and alignment coverage will be
              published.
            </p>
          )}
          {preview.requiresAlignmentAcknowledgment && (
            <label className="flex gap-2 items-start">
              <input
                type="checkbox"
                checked={acknowledged}
                disabled={busy || expired}
                onChange={e => setAcknowledged(e.target.checked)}
              />
              I acknowledge incomplete alignment: unresolved and explicitly unmatched classes
              contribute to overall totals, but not confirmed class trends.
            </label>
          )}
          {expired && <p role="alert">Preview expired. Request and review a fresh preview.</p>}
          <Button
            disabled={
              busy ||
              expired ||
              !state.finalized ||
              (preview.requiresAlignmentAcknowledgment && !acknowledged)
            }
            onClick={() => void act({ kind: 'publish', token: preview.token, acknowledged })}
          >
            {state.activePublication ? 'Republish reviewed snapshot' : 'Publish reviewed snapshot'}
          </Button>
        </div>
      )}
      {message && <p role="status">{message}</p>}
    </section>
  )
}

export function EditionPublication({
  showId,
  year,
  name,
}: {
  showId: string
  year: number
  name: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <details className="min-w-0" onToggle={event => setOpen(event.currentTarget.open)}>
      <summary className="cursor-pointer underline py-2">
        {year} · {name} — publication
      </summary>
      {open && <PublicationControls showId={showId} year={year} />}
    </details>
  )
}
